import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Mail, RefreshCw, LogOut, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountPending() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [checking, setChecking] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);

  useEffect(() => {
    checkAccountStatus();
  }, []);

  const checkAccountStatus = async () => {
    setChecking(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API}/owner/account-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data);
        
        // If account is now active, redirect to dashboard
        if (data.is_active) {
          navigate('/owner');
        }
      }
    } catch (error) {
      console.error('Error checking account status:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#F9FAFB] flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md px-4"
      >
        <div className="bg-white rounded-3xl shadow-soft p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-10 h-10 text-amber-600" />
          </div>
          
          {/* Title */}
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            {t('account.pendingTitle')}
          </h1>
          
          {/* Message */}
          <p className="text-slate-600 mb-6">
            {accountStatus?.message || t('account.pendingMessage')}
          </p>
          
          {/* User info */}
          {user && (
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-500">{t('account.loggedInAs')}</p>
              <p className="font-medium text-slate-900">{user.email}</p>
            </div>
          )}
          
          {/* Pending orders */}
          {accountStatus?.pending_orders?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <h3 className="font-medium text-amber-900 mb-2">
                {t('account.pendingOrders')}
              </h3>
              {accountStatus.pending_orders.map((order) => (
                <div key={order.order_id} className="flex justify-between items-center py-2 border-t border-amber-100 first:border-t-0">
                  <span className="font-mono text-sm text-amber-700">{order.order_id}</span>
                  <span className="font-medium text-amber-900">${order.total?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* What to expect */}
          <div className="bg-[#0A2540]/5 border border-[#E5E7EB] rounded-xl p-4 mb-6 text-left">
            <div className="flex items-start gap-2">
              <Mail className="w-5 h-5 text-[#0A2540] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900 mb-1">{t('account.whatToExpect')}</p>
                <p className="text-sm text-[#0A2540]">
                  {t('account.activationEmail')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={checkAccountStatus}
              disabled={checking}
              className="w-full btn-primary rounded-full"
              data-testid="check-status-btn"
            >
              {checking ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {t('account.checkStatus')}
            </Button>
            
            <Link to="/">
              <Button variant="outline" className="w-full rounded-full" data-testid="home-btn">
                <Home className="w-4 h-4 mr-2" />
                {t('common.backToHome')}
              </Button>
            </Link>
            
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-slate-500 hover:text-slate-700"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('auth.logout')}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
